import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import api from '../../services/api';
import { useAuth } from '../../context/AuthProvider';

interface ResetPasswordModalProps {
  open: boolean;
  onClose: () => void;
  username: string;
}

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({ open, onClose }) => {
  const { user } = useAuth();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Funções de validação
  const requirements = [
    {
      label: 'Mínimo 8 caracteres',
      test: (pw: string) => pw.length >= 8,
    },
    {
      label: 'Pelo menos 1 letra maiúscula',
      test: (pw: string) => /[A-Z]/.test(pw),
    },
    {
      label: 'Pelo menos 1 caractere especial (!@#$...)',
      test: (pw: string) => /[!@#$%^&*(),.?":{}|<>]/.test(pw),
    },
  ];

  const isStrongPassword = (password: string) => requirements.every(req => req.test(password));

  const handleSubmit = async () => {
    if (!user?.username) {
      setError('Usuário não encontrado');
      return;
    }

    if (!isStrongPassword(newPassword)) {
      setError('A nova senha não atende todos os requisitos.');
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await api.post('/auth/reset-password', {
        username: user.username,
        oldPassword,
        newPassword,
      });

      setSuccess('Senha alterada com sucesso!');
      setOldPassword('');
      setNewPassword('');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao alterar senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Typography variant="h6" textAlign="center">
          Trocar senha de {user?.name || user?.username || 'Usuário'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2} mt={1}>
          {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ width: '100%' }}>{success}</Alert>}

          <TextField
            label="Senha antiga"
            type="password"
            fullWidth
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
          />
          <TextField
            label="Nova senha"
            type="password"
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />

          <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle2" mb={1}>
              Requisitos da nova senha:
            </Typography>
            <List dense>
              {requirements.map((req) => {
                const passed = req.test(newPassword);
                return (
                  <ListItem key={req.label}>
                    <ListItemIcon>
                      {passed ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                      ) : (
                        <CancelIcon color="error" fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={req.label}
                      primaryTypographyProps={{
                        color: passed ? 'green' : 'red',
                        fontSize: 14,
                      }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          Cancelar
        </Button>
        <Button 
        onClick={handleSubmit} 
        variant="contained" 
        disabled={loading || !isStrongPassword(newPassword)}
        >
        {loading ? <CircularProgress size={24} /> : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResetPasswordModal;
